
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Image } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";

interface ImageLibraryPickerProps {
  onSelectImage: (url: string) => void;
  triggerButton?: React.ReactNode;
  selectedImage?: string;
  imageType?: "avatar" | "banner" | "all";
  className?: string;
}

const ImageLibraryPicker = ({
  onSelectImage,
  triggerButton,
  selectedImage,
  imageType = "all",
  className
}: ImageLibraryPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [avatars, setAvatars] = useState<string[]>([]);
  const [banners, setBanners] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setIsLoading(true);
        
        // Get images from the storage bucket
        const { data: avatarData, error: avatarError } = await supabase
          .storage
          .from('image_library')
          .list('avatars', {
            sortBy: { column: 'name', order: 'asc' }
          });
        
        const { data: bannerData, error: bannerError } = await supabase
          .storage
          .from('image_library')
          .list('banners', {
            sortBy: { column: 'name', order: 'asc' }
          });

        if (avatarError) throw avatarError;
        if (bannerError) throw bannerError;

        // Generate public URLs for avatars
        if (avatarData) {
          const avatarUrls = avatarData.map(file => {
            const { data } = supabase.storage
              .from('image_library')
              .getPublicUrl(`avatars/${file.name}`);
            return data.publicUrl;
          });
          setAvatars(avatarUrls);
        }

        // Generate public URLs for banners
        if (bannerData) {
          const bannerUrls = bannerData.map(file => {
            const { data } = supabase.storage
              .from('image_library')
              .getPublicUrl(`banners/${file.name}`);
            return data.publicUrl;
          });
          setBanners(bannerUrls);
        }
      } catch (error: any) {
        console.error("Error fetching images:", error);
        toast({
          variant: "destructive",
          title: "Error loading images",
          description: error.message || "Failed to load image library"
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchImages();
    }
  }, [isOpen, toast]);

  const handleSelectImage = (url: string) => {
    onSelectImage(url);
    setIsOpen(false);
  };

  // Determine which tabs to show based on imageType
  const showAvatars = imageType === "all" || imageType === "avatar";
  const showBanners = imageType === "all" || imageType === "banner";
  const defaultTab = showAvatars ? "avatars" : "banners";

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button 
            variant="outline" 
            className={cn("flex items-center", className)}
          >
            <Image className="h-4 w-4 mr-2" />
            Select image
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Image Library</DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        ) : (
          <Tabs defaultValue={defaultTab}>
            {(showAvatars && showBanners) && (
              <TabsList className="grid grid-cols-2 mb-4">
                <TabsTrigger value="avatars">Avatars</TabsTrigger>
                <TabsTrigger value="banners">Banners</TabsTrigger>
              </TabsList>
            )}
            
            {showAvatars && (
              <TabsContent value="avatars">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                  {avatars.length > 0 ? (
                    avatars.map((url, index) => (
                      <div 
                        key={index} 
                        className={cn(
                          "aspect-square rounded-md overflow-hidden cursor-pointer border-2 hover:border-primary transition-colors",
                          selectedImage === url ? "border-primary" : "border-transparent"
                        )}
                        onClick={() => handleSelectImage(url)}
                      >
                        <img 
                          src={url} 
                          alt={`Avatar ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8 text-muted-foreground">
                      No avatar images found in the library
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
            
            {showBanners && (
              <TabsContent value="banners">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {banners.length > 0 ? (
                    banners.map((url, index) => (
                      <div 
                        key={index} 
                        className={cn(
                          "aspect-[3/1] rounded-md overflow-hidden cursor-pointer border-2 hover:border-primary transition-colors",
                          selectedImage === url ? "border-primary" : "border-transparent"
                        )}
                        onClick={() => handleSelectImage(url)}
                      >
                        <img 
                          src={url} 
                          alt={`Banner ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8 text-muted-foreground">
                      No banner images found in the library
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImageLibraryPicker;
